import { enqueueSnackbar } from '~/utils/notifications';
import { useIntl } from 'react-intl';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import {
    DicomBase,
    GenerationModelDetails,
    ObjectStatus,
} from '~/domain/dicom/dicomBase';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import type { Image3D } from '~/domain/patient/image3d';
import type { StructureSet } from '~/domain/patient/structureset';
import type { DatasetEnvironmentRegistry } from '~/engine/scene/DatasetEnvironmentRegistry';
import { ImageSeriesManifest } from '~/domain/patient/imageSeriesManifest';
import {
    ImportResult,
    InterpolatedImageInfo,
    ToRepositoryOptions,
} from '~/domain/services/dicomProcessor';
import {
    dicomProcessorState,
    filesProcessedState,
    filesToProcessState,
} from '~/state/atoms/dicomProcessorAtoms';
import {
    interpolationAcknowledgementState,
    setPendingInterpolationResolver,
} from '~/state/atoms/interpolationAtoms';
import { patientRepositoryState } from '~/state/atoms/patientAtoms';
import {
    exportableItemsCountState,
    pseudonymizeState,
} from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { createSkipReport, processFile } from '~/utils/io/files/FileHandler';
import { expandArchives } from '~/utils/io/files/archive';
import type { FileTreeLimits } from '~/utils/io/files/fileTree';
import { archiveRefusalText } from '~/containers/patient-list/import/model/importText';
import { formatImportMessage } from '~/containers/patient-list/import/model/importMessage';
import { emitSkipReportToasts } from '~/utils/io/files/skipReportToasts';
import usePatientConflictPrompt from '~/hooks/patient/usePatientConflictPrompt';
import usePatientGuard from '~/hooks/patient/usePatientGuard';
import useUnloadPatient from '~/hooks/patient/useUnloadPatient';
import { claimIngest, releaseIngest } from '~/hooks/dicomIngestOwnership';
import { useCommitImportToRepository } from '~/hooks/domain/useCommitImport';
import useBusyIndicator from '~/hooks/useBusyIndicator';
import { useBumpDicomProcessorVersion } from '~/hooks/useDicomProcessorVersion';
import { useRefuseWhileViewerOpening } from '~/hooks/useRefuseWhileViewerOpening';
import { useLeavePatientRoute } from '~/routes/useLeavePatientRoute';
import { flushGridContourEdits } from '~/hooks/gridContourFlush';
import { localization } from '~/i18n';
import { redactDicomUids } from '~/utils/phiRedaction';

/** Local loads stay in the browser, so no upload request cap applies. */
const UNCAPPED: FileTreeLimits = {
    maxBytes: Number.POSITIVE_INFINITY,
    partFramingBytes: 0,
};

type ProcessDicomFilesParams = {
    onFileProcessed: (file: DicomBase) => void;
};

/** The registry surface the grid-population helper writes to. */
type GridRegistryTarget = Pick<
    DatasetEnvironmentRegistry,
    'register' | 'saveAllResidentChanges'
>;

/**
 * Publishes imported volumes to the viewport-grid registry. An import that
 * carries a structure set can go on to replace the active set, freeing the
 * SDF textures that set's unsaved grid edits live in, so the edits are
 * saved to the domain first. Each registration is isolated: a malformed
 * series UID makes the registry throw, and that must never fail an import
 * whose patient data is already committed to the repository. The offending
 * dataset is skipped and logged (no PHI in the message); the rest still
 * register.
 */
export function registerImportedDatasets(
    registry: GridRegistryTarget,
    images: ReadonlyArray<{ image: Image3D; patientId: string }>,
    structureSets: ReadonlyArray<StructureSet>
): void {
    if (structureSets.length > 0) {
        flushGridContourEdits(
            registry,
            localization.t('contouring.gridSaveFailed.editsMayBeLost')
        );
    }
    for (const { image, patientId } of images) {
        try {
            registry.register(image, patientId);
        } catch (error) {
            console.error(
                'Skipped registering an imported image',
                redactDicomUids(error)
            );
        }
    }
}

/** A patient as the registry publish reads it: its id and its images. */
interface ImageOwner {
    readonly patientId: string;
    readonly images: ReadonlyArray<Image3D>;
}

/**
 * Publishes each volume of a committed import to the viewport-grid
 * registry, keyed by the patient in `patients` that now owns it; an image
 * no patient owns is not published. Never throws: the import's patient data
 * is already committed, and a caller that rolls a commit back on an error
 * would delete a completed import over grid-only bookkeeping. A failure is
 * logged with no PHI.
 */
export function registerImportResult(
    registry: GridRegistryTarget,
    patients: ReadonlyArray<ImageOwner>,
    result: Pick<ImportResult, 'images' | 'structureSets'>
): void {
    try {
        const owned = result.images.flatMap((image) => {
            const owner = patients.find((patient) =>
                patient.images.includes(image)
            );
            return owner ? [{ image, patientId: owner.patientId }] : [];
        });
        registerImportedDatasets(registry, owned, result.structureSets);
    } catch (error) {
        console.error(
            'Failed to publish the import to the viewport-grid registry',
            redactDicomUids(error)
        );
    }
}

export type ProcessFilesOptions = {
    allowMultiplePatients?: boolean;
    /**
     * Declares the caller to be the holder of the exclusive-feed lock — the
     * open-in-viewer loader, or the on-demand load that brings one more of
     * the patient's datasets into an open session. Both take the lock before
     * they feed, so without this they would refuse themselves. Every other
     * caller is refused while the lock is held.
     */
    holdsOpenLock?: boolean;
    /**
     * Manifest geometry for image series being opened progressively, keyed by
     * Series Instance UID. Only the open-in-viewer loader sets it; every other
     * caller commits fully parsed series and must not.
     */
    imageManifests?: ReadonlyMap<string, ImageSeriesManifest>;
    /**
     * The identity the connector stores for each structure of a structure
     * set being opened from it (see {@link ToRepositoryOptions.roiUids}).
     * Only the flows that open connector data set it.
     */
    roiUids?: ToRepositoryOptions['roiUids'];
};

export default function useProcessDicomFiles(
    params: ProcessDicomFilesParams = { onFileProcessed: () => {} }
) {
    const intl = useIntl();
    const dicomProcessor = useRecoilValue(dicomProcessorState);
    const patientRepository = useRecoilValue(patientRepositoryState);
    const setFilesToProcess = useSetRecoilState(filesToProcessState);
    const setFilesProcessed = useSetRecoilState(filesProcessedState);
    const setExportableItemsCount = useSetRecoilState(
        exportableItemsCountState
    );
    const setInterpolationAcknowledgement = useSetRecoilState(
        interpolationAcknowledgementState
    );
    const resetPseudonymize = useResetRecoilState(pseudonymizeState);

    const { checkPatientConflict } = usePatientGuard();
    const { unloadCurrentPatient } = useUnloadPatient();
    const commitImportToRepository = useCommitImportToRepository();
    const bumpDicomProcessorVersion = useBumpDicomProcessorVersion();
    const refuseWhileViewerOpening = useRefuseWhileViewerOpening();
    const leavePatientRoute = useLeavePatientRoute();

    const { askPatientConflict } = usePatientConflictPrompt();
    const { hideBusy } = useBusyIndicator();

    function resetProcessingState() {
        setFilesProcessed(0);
        setFilesToProcess(undefined);
    }

    /**
     * Opens the blocking acknowledgement dialog and resolves once
     * the user clicks Acknowledge. Interpolation is a clinically
     * significant transformation, so we require an explicit, non-
     * dismissible confirmation rather than a transient snackbar.
     */
    function awaitInterpolationAcknowledgement(
        images: InterpolatedImageInfo[]
    ): Promise<void> {
        return new Promise<void>((resolve) => {
            setPendingInterpolationResolver(resolve);
            setInterpolationAcknowledgement({ open: true, images });
        });
    }

    async function handleFilesProcessed(
        options?: ToRepositoryOptions
    ): Promise<ImportResult | undefined> {
        // Pre-commit: a mid-mutation throw may already have added the new row.
        const loadedBeforeCommit = new Set(
            patientRepository.getAll().map((p) => p.dataSet.PatientID)
        );
        try {
            // Every ingest commit goes through this helper — see its doc
            // for why an unwrapped toRepository throws part-way through and
            // leaves the aggregate populated but invisible to the views.
            const result = commitImportToRepository(() =>
                dicomProcessor.toRepository(patientRepository, options)
            );
            // A choice to send identifiable data is made for one patient, so
            // opening another turns de-identification back on.
            const opensAnotherPatient = patientRepository
                .getAll()
                .some((p) => !loadedBeforeCommit.has(p.dataSet.PatientID));
            if (opensAnotherPatient) {
                resetPseudonymize();
            }

            // Publish each imported volume to the viewport-grid registry,
            // keyed by its owning patient. A re-import replaces the stale
            // entry, so the grid never renders old data.
            if (result) {
                registerImportResult(
                    getDatasetEnvironmentRegistry(),
                    patientRepository.getAll(),
                    result
                );
            }

            // Block on user acknowledgement of interpolated series
            // before returning. The image list refresh below runs
            // only after acknowledgement so the new images do not
            // appear behind the modal. The processing overlay is
            // dismissed first because it renders at z-index 4000
            // and would otherwise sit above the dialog.
            if (result?.interpolatedImages.length) {
                resetProcessingState();
                await awaitInterpolationAcknowledgement(
                    result.interpolatedImages
                );
            }

            // Notify user about series that could not be reconstructed
            if (result?.skippedSeries.length) {
                for (const info of result.skippedSeries) {
                    const description = info.seriesDescription
                        ? `"${info.seriesDescription}"`
                        : 'Image series';
                    enqueueSnackbar(
                        `${description} could not be reconstructed: ${info.reason}`,
                        {
                            variant: 'warning',
                        }
                    );
                }
            }
            return result;
        } catch (error: Error | unknown) {
            // Drop parsed patients the repository did not hold pre-commit;
            // the loaded patient's marks cover committed objects and stay.
            for (const patientId of dicomProcessor.getPatientIds()) {
                if (loadedBeforeCommit.has(patientId)) {
                    continue;
                }
                dicomProcessor.removePatientData(patientId);
                const committed = patientRepository.getByPatientId(patientId);
                if (committed) {
                    patientRepository.delete(committed.id);
                }
            }
            bumpDicomProcessorVersion();
            if (error instanceof Error) {
                handleError(error, `Failed to process files: ${error.message}`);
            } else {
                handleError(error, 'Failed to process files');
            }
        } finally {
            resetProcessingState();
        }
    }

    function checkForExistingRTStructs(sliceData: DicomBase): boolean {
        const existingPatient = patientRepository.getByPatientId(
            sliceData.datasetWrapper.PatientID
        );
        if (existingPatient) {
            const existingStructureSet =
                existingPatient.getStructureSetWithSopInstanceUid(
                    sliceData.sopInstanceUid
                );
            if (existingStructureSet) {
                return true;
            } else {
                return false;
            }
        }
        return false;
    }

    function checkForExistingRTDoses(sliceData: DicomBase): boolean {
        const existingPatient = patientRepository.getByPatientId(
            sliceData.datasetWrapper.PatientID
        );
        if (existingPatient) {
            const existingDose = existingPatient.getDoseWithSopInstanceUid(
                sliceData.sopInstanceUid
            );
            if (existingDose) {
                return true;
            } else {
                return false;
            }
        }
        return false;
    }

    async function processFiles(
        files: File[],
        objectStatus: ObjectStatus,
        model?: GenerationModelDetails,
        options?: ProcessFilesOptions
    ) {
        // Two patients parsed into the shared processor would interleave, and
        // a discard would drop the other caller's results. Gates the start of
        // an import, not the processor: one already parsing runs to
        // completion. The reformat flow bypasses this and carries the same
        // guard at its own entry.
        if (!options?.holdsOpenLock && refuseWhileViewerOpening()) {
            return undefined;
        }
        // Mutual exclusion: whichever parse starts second is refused.
        if (!claimIngest()) {
            enqueueSnackbar(intl.formatMessage({ id: 'ingestBusy' }), {
                variant: 'warning',
            });
            return undefined;
        }

        // Reset both counters so this phase is self-contained.
        // `filesToProcess` is overwritten anyway, but `filesProcessed`
        // would otherwise carry leftover increments from a prior phase
        // (e.g. import → composite-result back-to-back) and produce a
        // numerator larger than the denominator in the overlay.
        // What a discard restores the processor to.
        const heldBeforeImport = dicomProcessor.heldSopInstanceUids();
        try {
            // Archives are expanded up front so the progress total counts
            // the files inside them, not the archive itself.
            // Show the overlay before expanding, so a slow archive is covered too.
            setFilesToProcess(files.length);
            const expansion = await expandArchives(files, UNCAPPED);
            if (expansion.status !== 'ok') {
                enqueueSnackbar(
                    formatImportMessage(intl, archiveRefusalText(expansion)),
                    { variant: 'warning' }
                );
                resetProcessingState();
                return undefined;
            }
            setFilesProcessed(0);
            setFilesToProcess(expansion.files.length);
            const skipReport = createSkipReport();
            for await (const file of expansion.files) {
                const dicomData = await processFile(
                    file,
                    dicomProcessor,
                    objectStatus,
                    model,
                    skipReport
                );
                if (dicomData) {
                    if (checkForExistingRTStructs(dicomData)) {
                        enqueueSnackbar(
                            'This RTStruct has already been loaded.',
                            {
                                variant: 'warning',
                            }
                        );
                        continue;
                    }
                    if (checkForExistingRTDoses(dicomData)) {
                        enqueueSnackbar(
                            'This RTDose has already been loaded.',
                            {
                                variant: 'warning',
                            }
                        );
                        continue;
                    }
                    params.onFileProcessed(dicomData);
                }
                setFilesProcessed((prevFilesLoaded) => prevFilesLoaded + 1);
            }

            emitSkipReportToasts(skipReport);

            // Check for patient conflict before committing to the
            // repository. The files have been parsed into
            // dicomProcessor but not yet added to patientRepository.
            // Skip if the caller already resolved the conflict
            // (e.g. an early "load both" in the connector import flow).
            const incomingPatientIds = dicomProcessor.getPatientIds();
            const guardResult = options?.allowMultiplePatients
                ? ({ action: 'allow' } as const)
                : checkPatientConflict(incomingPatientIds);

            let replacedLoadedPatient = false;
            if (guardResult.action === 'conflict') {
                // Dismiss the processing overlay and a caller's busy
                // indicator, so the conflict dialog is not hidden behind
                // them (both render at z-index 4000).
                resetProcessingState();
                hideBusy();

                const userAction = await askPatientConflict(guardResult);

                if (userAction === 'discard') {
                    // Drop everything this import parsed, for the loaded
                    // patients too, without committing; keep what the
                    // processor held before, so it is not imported twice.
                    dicomProcessor.removeObjectsExcept(heldBeforeImport);
                    resetProcessingState();
                    return undefined;
                }

                if (userAction === 'loadBoth') {
                    // Non-clinical: user chose to load both patients.
                    // Commit with relaxed multi-patient guard.
                    return handleFilesProcessed({
                        allowMultiplePatients: true,
                        imageManifests: options?.imageManifests,
                        roiUids: options?.roiUids,
                    });
                }

                // 'switch' — unload existing patient, then commit.
                // Preserve the DicomProcessor state because it still
                // holds the incoming patient data that toRepository()
                // needs to commit.
                unloadCurrentPatient({ preserveDicomProcessor: true });

                // Remove the old patients' data from the processor.
                // The processor accumulates all parsed patients; we
                // only want to commit the incoming one(s).
                guardResult.loadedPatientIds.forEach((patientId) => {
                    dicomProcessor.removePatientData(patientId);
                });
                replacedLoadedPatient = true;
            }

            const result = await handleFilesProcessed({
                allowMultiplePatients: options?.allowMultiplePatients,
                imageManifests: options?.imageManifests,
                roiUids: options?.roiUids,
            });
            // The patient the route names is gone, whether or not the commit
            // stood. A worklist open has already routed to its own patient.
            if (replacedLoadedPatient && !options?.holdsOpenLock) {
                leavePatientRoute();
            }
            return result;
        } catch (error) {
            handleError(error, 'Error processing files');
            resetProcessingState();
        } finally {
            releaseIngest();
        }
    }

    const processImportedFiles = async (
        files: File[],
        options?: ProcessFilesOptions
    ) => processFiles(files, ObjectStatus.IMPORTED, undefined, options);

    const processCreatedFiles = async (
        files: File[],
        model?: GenerationModelDetails
    ) => {
        const processed = await processFiles(files, ObjectStatus.NEW, model);
        // Nothing was committed when the processing did not produce a result
        // (refused, discarded, or failed), so there is no new exportable item.
        if (processed) {
            setExportableItemsCount((prevCount) => prevCount + 1);
        }
        return processed;
    };

    return {
        processImportedFiles,
        processCreatedFiles,
    };
}
